import { ArrayInput, FunctionField, Record } from "react-admin";
import { ProtectedElement } from "../../common/field-permissions/ProtectedElement";
import resourceNames from "../../../common/data/resourceNames";
import { useForm, useFormState } from "react-final-form";
import {
  RecordStatus,
  VehicleViewDto,
  VirtualTenantVehicleRangeDto,
} from "../../../common/data/service-proxies";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Button as MuiButton,
} from "@material-ui/core";
import { Event as EventIcon } from "@material-ui/icons";
import { useState } from "react";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import ProLogsDateFnsUtils from "../../../common/datetime/ProLogsDateFnsUtils";
import { VehiclePeriodListIterator } from "../../common/list/compact-list/VehiclePeriodListIterator";

interface Props { }

export const VehicleWithPeriodSelectionGrid = (props: Props) => {
  const form = useForm();
  const { values } = useFormState();
  const tenantIdState = form.getFieldState("tenantId");
  const [scheduleVehicle, setScheduleVehicle] = useState<
    (VehicleViewDto & Record) | null
  >(null);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [endTime, setEndTime] = useState<Date | null>(null);
  const datesInvalid =
    !!startTime && !!endTime && startTime.getTime() > endTime.getTime();

  const handleOpenSchedule = (record: VehicleViewDto & Record) => {
    const current: VirtualTenantVehicleRangeDto[] = (values as any)
      .vehicles ?? [];
    const existing = current.find((x) => x.vehicleId === record.id);
    setScheduleVehicle(record);
    setStartTime(existing?.startTime ? new Date(existing.startTime) : null);
    setEndTime(existing?.endTime ? new Date(existing.endTime) : null);
  };

  const applyCurrentTime = (date: Date | null) => {
    if (!date) {
      return null;
    }
    const now = new Date();
    const withTime = new Date(date);
    withTime.setHours(
      now.getHours(),
      now.getMinutes(),
      now.getSeconds(),
      now.getMilliseconds()
    );
    return withTime;
  };

  const handleCloseSchedule = () => {
    setScheduleVehicle(null);
    setStartTime(null);
    setEndTime(null);
  };

  const handleSaveSchedule = () => {
    if (!scheduleVehicle || datesInvalid) {
      return;
    }
    const current: VirtualTenantVehicleRangeDto[] = ((values as any)
      .vehicles ?? []) as VirtualTenantVehicleRangeDto[];
    const other = current.filter((x) => x.vehicleId !== scheduleVehicle.id);
    const startWithTime = applyCurrentTime(startTime);
    const endWithTime = applyCurrentTime(endTime);
    const updated: VirtualTenantVehicleRangeDto = {
      vehicleId: scheduleVehicle.id ?? undefined,
      startTime: startWithTime ? startWithTime.toISOString() : null,
      endTime: endWithTime ? endWithTime.toISOString() : null,
    };
    form.change("vehicles", [...other, updated]);
    handleCloseSchedule();
  };

  const handleSaveScheduleForAll = () => {
    if (datesInvalid) {
      return;
    }
    const current: VirtualTenantVehicleRangeDto[] = ((values as any)
      .vehicles ?? []) as VirtualTenantVehicleRangeDto[];
    if (!current.length) {
      handleCloseSchedule();
      return;
    }
    const startWithTime = applyCurrentTime(startTime);
    const endWithTime = applyCurrentTime(endTime);
    const updatedAll = current.map((x) => ({
      ...x,
      startTime: startWithTime ? startWithTime.toISOString() : null,
      endTime: endWithTime ? endWithTime.toISOString() : null,
    }));
    form.change("vehicles", updatedAll);
    handleCloseSchedule();
  };

  const renderVehicleLabel = (record: VehicleViewDto & Record) => {
    const current: VirtualTenantVehicleRangeDto[] = (values as any)
      .vehicles ?? [];
    const range = current.find((x) => x.vehicleId === record.id);
    if (!range) {
      return record.name;
    }

    const fmt = (iso: string | null | undefined) => {
      if (!iso) {
        return null;
      }
      const d = new Date(iso);
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      const yyyy = d.getFullYear();
      return `${mm}/${dd}/${yyyy}`;
    };

    const start = fmt(range.startTime ?? null);
    const end = fmt(range.endTime ?? null);

    if (!start && !end) {
      return record.name;
    }
    if (!start && end) {
      return `${record.name} (${`Past - ${end}`})`;
    }
    if (start && !end) {
      return `${record.name} (${`${start} - Present`})`;
    }
    return `${record.name} (${`${start} - ${end}`})`;
  };

  if (!tenantIdState?.value) {
    return null;
  }

  return (
    <ProtectedElement source="vehicles" label="" {...props}>
      <>
        <ArrayInput source="vehicles">
          <VehiclePeriodListIterator
            reference={resourceNames.ADMIN_VEHICLES_VIEW}
            source="vehicles"
            sort={{ field: "name", order: "asc" }}
            filter={{
              tenantId: tenantIdState?.value,
              status: RecordStatus.Active,
            }}
            itemIdField="vehicleId"
            renderSelectedExtra={(record) => (
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  width: "100%",
                }}
              >
                <IconButton
                  size="small"
                  onClick={() =>
                    handleOpenSchedule(record as VehicleViewDto & Record)
                  }
                >
                  <EventIcon fontSize="small" />
                </IconButton>
              </div>
            )}
          >
            <FunctionField
              label="resources.admin-vehicles.labels.vehicleId"
              render={(record?: Record) =>
                record
                  ? renderVehicleLabel(record as VehicleViewDto & Record)
                  : ""
              }
            />
          </VehiclePeriodListIterator>
        </ArrayInput>
        <MuiPickersUtilsProvider utils={ProLogsDateFnsUtils}>
          <Dialog
            open={!!scheduleVehicle}
            onClose={handleCloseSchedule}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Set visibility period</DialogTitle>
            <DialogContent>
              <DatePicker
                label="Start date"
                value={startTime}
                onChange={(date) => setStartTime(date as Date | null)}
                format="MMMM do yyyy"
                clearable
                fullWidth
                inputVariant="outlined"
                margin="normal"
                maxDate={endTime ?? undefined}
              />
              <DatePicker
                label="End date"
                value={endTime}
                onChange={(date) => setEndTime(date as Date | null)}
                format="MMMM do yyyy"
                clearable
                fullWidth
                inputVariant="outlined"
                margin="normal"
                minDate={startTime ?? undefined}
              />
            </DialogContent>
            <DialogActions>
              <MuiButton onClick={handleCloseSchedule} color="default">
                Cancel
              </MuiButton>
              <MuiButton
                onClick={handleSaveSchedule}
                color="primary"
                variant="contained"
                disabled={datesInvalid}
              >
                Save
              </MuiButton>
              <span
                style={{
                  borderLeft: "1px solid rgba(0,0,0,0.12)",
                  height: 24,
                  margin: "0 8px",
                }}
              />
              <MuiButton
                onClick={handleSaveScheduleForAll}
                color="primary"
                variant="outlined"
                disabled={datesInvalid}
              >
                Save for all vehicles
              </MuiButton>
            </DialogActions>
          </Dialog>
        </MuiPickersUtilsProvider>
      </>
    </ProtectedElement>
  );
}

